import { useLocalStorage, useSetState, useThunkDispatch } from '@domain-group/react-hooks'

import { defaultRequestOptions } from '@hooks/useCurrentUser'

import * as agencyActions from '@redux/modules/agency'
import * as brandActions from '@redux/modules/brand'
import * as currentUserActions from '@redux/modules/currentUser'
import * as providerActions from '@redux/modules/provider'
import * as userActions from '@redux/modules/user'
import type { AppDispatch } from '@redux/store'

import type {
  AgencyModel, BrandModel, ProviderModel, UserModel,
} from '@models/types'

export type EntityModel = AgencyModel | BrandModel | ProviderModel | UserModel

const entityActionDefinitions = {
  Agency: agencyActions,
  Brand: brandActions,
  Provider: providerActions,
  User: userActions,
} as const

type DefaultState = {
  loading: boolean,
  supportToken: string,
}

type SetState = (state: Partial<DefaultState>) => void

type GenerateEntitySupportTokenParams = {
  dispatch: AppDispatch,
  entity: EntityModel,
}

const generateEntitySupportToken = (params: GenerateEntitySupportTokenParams) => {
  const { dispatch, entity } = params

  // @ts-expect-error TS7053
  const { generateSupportToken: generateTokenFn } = entityActionDefinitions[entity.type]

  return dispatch(generateTokenFn(entity))
}

type ImpersonateUserParams = {
  dispatch: AppDispatch,
  redirectPath?: string,
  setPostUnlinkImpersonationRedirect: (redirect: string) => void,
  supportToken: string,
}

const impersonateUserWithToken = (params: ImpersonateUserParams) => {
  const {
    dispatch,
    redirectPath,
    setPostUnlinkImpersonationRedirect,
    supportToken,
  } = params

  const { clearStore, hydrateCurrentUser: hydrateFn } = currentUserActions
  const { impersonateUserWithToken: impersonateFn } = userActions

  return dispatch(impersonateFn(supportToken, defaultRequestOptions)).then((response) => {
    // @ts-expect-error TS2339
    const { data, success } = response

    if (success){
      dispatch(clearStore())
      dispatch(hydrateFn(data))
      setPostUnlinkImpersonationRedirect(window.location.href)
      window.location.hash = redirectPath || '/'
    }

    return response
  })
}

type GenerateTokenAndImpersonateParams = {
  dispatch: AppDispatch,
  entity: EntityModel,
  redirectPath?: string,
  setPostUnlinkImpersonationRedirect: (redirect: string) => void,
  setState: SetState,
}

const generateTokenAndImpersonate = async (params: GenerateTokenAndImpersonateParams) => {
  const {
    dispatch,
    entity,
    redirectPath,
    setPostUnlinkImpersonationRedirect,
    setState,
  } = params

  setState({ loading: true })

  // Generate Token
  const tokenResult = await generateEntitySupportToken({
    dispatch,
    entity,
  })

  if (!tokenResult.success || !tokenResult.data?.support_token){
    setState({ loading: false })
    return tokenResult
  }

  // Impersonate
  const impersonationResult = await impersonateUserWithToken({
    dispatch,
    redirectPath,
    setPostUnlinkImpersonationRedirect,
    supportToken: tokenResult.data.support_token,
  })

  setState({ loading: false })

  return impersonationResult
}

type UnlinkImpersonationParams = {
  dispatch: AppDispatch,
  getPostUnlinkImpersonationRedirect: () => string,
  unlinkParams: {
    skipRedirect?: boolean,
    userId: number,
  },
}

const unlinkImpersonation = (params: UnlinkImpersonationParams) => {
  const {
    dispatch, getPostUnlinkImpersonationRedirect, unlinkParams: { skipRedirect, userId },
  } = params

  const { clearStore, hydrateCurrentUser: hydrateFn } = currentUserActions
  const { unlinkImpersonation: unlinkFn } = userActions

  return dispatch(unlinkFn({ userId, options: defaultRequestOptions })).then((response) => {
    // @ts-expect-error TS2339
    const { success, data } = response

    if (skipRedirect) return response

    if (success){
      dispatch(clearStore())
      dispatch(hydrateFn(data))
    }

    const postImpersonationRedirect = getPostUnlinkImpersonationRedirect()

    if (postImpersonationRedirect){
      window.location.href = postImpersonationRedirect
      return response
    }

    window.location.hash = '/'

    return response
  })
}

const defaultState = {
  loading: false,
  supportToken: '',
}

function useEntityImpersonation(entity?: EntityModel) {
  const [state, setState] = useSetState(defaultState)
  const { loading, supportToken } = state

  const dispatch = useThunkDispatch()

  // Store user current location before impersonating
  // so that we can redirect back after they unlink
  const localStoragePayload = useLocalStorage('postUnlinkImpersonationRedirect')
  const {
    callbacks: {
      getValue: getPostUnlinkImpersonationRedirect,
      setValue: setPostUnlinkImpersonationRedirect,
    },
  } = localStoragePayload

  return {
    callbacks: {
      generateEntitySupportToken: (
        payload: {
          entity: EntityModel,
        },
      ) => generateEntitySupportToken({
        dispatch,
        entity: payload?.entity,
      }),
      generateTokenAndImpersonate: (
        payload: {
          entity: EntityModel,
          redirectPath?: string,
        },
      ) => generateTokenAndImpersonate({
        dispatch,
        redirectPath: payload?.redirectPath,
        setPostUnlinkImpersonationRedirect,
        setState,
        entity: payload?.entity,
      }),
      impersonateUserWithToken: (suppliedToken?: string) => impersonateUserWithToken({
        dispatch,
        setPostUnlinkImpersonationRedirect,
        supportToken: suppliedToken || supportToken,
      }),
      setState,
      unlinkImpersonation: (unlinkParams: { userId: number, skipRedirect?: boolean }) => unlinkImpersonation({
        dispatch, getPostUnlinkImpersonationRedirect, unlinkParams,
      }),
    },
    loading,
    realhubPlatform: entity?.realhub_platform || 'legacy',
    supportToken,
  }
}

export type UseEntityImpersonationPayload = ReturnType<typeof useEntityImpersonation>

export default useEntityImpersonation
